import React, { useEffect, useState } from "react";
import useWebSocket from "./useWebSocket";
import useAudioPlayer from "./useAudioPlayer";
import { mulaw } from "alawmulaw";

//const WEBSOCKET_SERVER_URL = "ws:localhost:5000";
const WEBSOCKET_SERVER_URL = process.env.REACT_APP_IVAS_SERVER_URL;

const useIvasSimulation = (
  callSid,
  handleTextMessage,
  onIvasError,
  onFinish
) => {
  const { sendMessage, close, status, connect } = useWebSocket(
    WEBSOCKET_SERVER_URL,
    handleMessage
  );

  const { playAudioBuffer, init, getAudioContext, isReady, stopPlaying, skip } =
    useAudioPlayer(() => {
      sendMessage(
        JSON.stringify({
          event: "mark",
          mark: {
            name: "partial-done",
          },
        })
      );
    });

  const [interrupted, setInterrupted] = useState(false);

  const connectToIvasServer = () => {
    connect();
  };

  const askIvas = (data) => {
    console.log("asking ivas with:", data);
    if (isReady) {
      const configs = {
        event: "start",
        start: {
          callSid,
          customParameters: {
            ...data,
            isSimulation: true,
          },
        },
      };
      sendMessage(JSON.stringify(configs));
    } else {
      console.log("Audio player is not ready");
    }
  };

  useEffect(() => {
    if (!isReady()) {
      init();
    }
  }, [init]);

  useEffect(() => {
    return () => {
      close();
    };
  }, []);

  function ulawToPcm16(audioDataArrayBuffer) {
    const audioData = new Uint8Array(audioDataArrayBuffer);
    const pcm16 = mulaw.decode(audioData);
    return pcm16.buffer;
  }

  function base64ToArrayBuffer(base64) {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  function createAudioBufferFromArrayBuffer(audioContext, arrayBuffer) {
    const numChannels = 1;
    const sampleRate = 8000;
    const numSamples = arrayBuffer.byteLength / 2; // 2 bytes per sample for 16-bit PCM
    const audioBuffer = audioContext.createBuffer(
      numChannels,
      numSamples,
      sampleRate
    );
    const audioData = new Float32Array(numSamples);

    const int16View = new Int16Array(arrayBuffer);
    for (let i = 0; i < numSamples; i++) {
      audioData[i] = int16View[i] / 0x8000; // Convert from 16-bit PCM to float32
    }

    audioBuffer.getChannelData(0).set(audioData);
    return audioBuffer;
  }

  function handleMessage(message) {
    const parsedMessage = JSON.parse(message);
    console.log("parsed message", parsedMessage);

    if (parsedMessage.event === "media") {
      handleTextMessage(parsedMessage.media.text);

      if (parsedMessage.errors?.length) {
        console.log("got error");
        onIvasError(parsedMessage.errors);
      }

      const audioDataArrayBuffer = base64ToArrayBuffer(
        parsedMessage.media.payload
      );
      const pcm16AudioData = ulawToPcm16(audioDataArrayBuffer);
      const audioContext = getAudioContext();
      let audioBuffer = null;
      try {
        audioBuffer = createAudioBufferFromArrayBuffer(
          audioContext,
          pcm16AudioData
        );
      } catch (e) {
        console.log("error", e);
        skip(false);
      }

      if (!interrupted && audioBuffer) {
        playAudioBuffer(audioBuffer);
      }
    } else if (
      parsedMessage.event === "mark" &&
      parsedMessage.mark?.name === "finish"
    ) {
      console.log("FINISH !");
      onFinish();
    } else if (parsedMessage.event === "error" && parsedMessage.errors) {
      onIvasError(parsedMessage.errors);
    }
  }

  const interrupt = () => {
    stopPlaying();
    onFinish();
    close();
  };

  return { askIvas, status, connectToIvasServer, interrupt, skip };
};

export default useIvasSimulation;
